/**
 * Sanitizes AI-generated content by stripping raw HTML tags while preserving markdown syntax.
 *
 * This prevents XSS attacks from prompt injection where the LLM is manipulated
 * into returning malicious HTML (e.g., <script>, <img onerror=...>).
 *
 * The markdown renderer (ngx-markdown) will still convert markdown syntax to safe HTML.
 * This function ensures only markdown—not raw HTML—reaches the renderer.
 */
export function sanitizeAiContent(text: string | null | undefined): string {
  if (!text) {
    return "";
  }

  // Protect code blocks (``` ... ```) by replacing them with placeholders
  const codeBlocks: string[] = [];
  let result = text.replace(/```[\s\S]*?```/g, match => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  // Protect inline code (` ... `) by replacing with placeholders
  const inlineCode: string[] = [];
  result = result.replace(/`[^`]+`/g, match => {
    inlineCode.push(match);
    return `__INLINE_CODE_${inlineCode.length - 1}__`;
  });

  // Strip all HTML tags (including self-closing and with attributes)
  result = result.replace(/<[^>]*>/g, "");

  // Restore inline code placeholders
  inlineCode.forEach((code, i) => {
    result = result.replace(`__INLINE_CODE_${i}__`, code);
  });

  // Restore code block placeholders
  codeBlocks.forEach((block, i) => {
    result = result.replace(`__CODE_BLOCK_${i}__`, block);
  });

  return result;
}
